import type { CognitoUser } from 'amazon-cognito-identity-js';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Pool from '@/services/auth/userPool';
import { useAuthStore } from '@/stores/slices/useAuthStore';
import { clearAllSessionData } from '@/utils/session';

const SESSION_TIMEOUT = 30 * 60 * 1000;
const ACTIVITY_DEBOUNCE = 60 * 1000;

export const useActivityTracking = (): void => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const lastActivity = useAuthStore((state) => state.lastActivity);
  const updateActivity = useAuthStore((state) => state.updateActivity);
  const setUser = useAuthStore((state) => state.setUser);
  const isSessionCacheValid = useAuthStore((state) => state.isSessionCacheValid);
  const clearSessionCache = useAuthStore((state) => state.clearSessionCache);

  useEffect(() => {
    if (!user) return;

    /**
     * Handles session expiration by:
     * 1. Signing out from Cognito
     * 2. Clearing all session data (cookies, storage, cache)
     * 3. Resetting auth store
     * 4. Redirecting to login page
     * NO alert is shown - silent redirect to login
     */
    const handleSessionExpiration = async (): Promise<void> => {
      // Sign out from Cognito
      const current = Pool.getCurrentUser() as CognitoUser | null;
      if (current) current.signOut();

      // Clear all session-related data
      await clearAllSessionData();
      clearSessionCache();

      // Reset auth state
      setUser(null);

      // Redirect to login page
      navigate('/login', { replace: true });
    };

    const checkTimeout = async (): Promise<void> => {
      // Check if session cache is still valid
      if (!isSessionCacheValid()) {
        await handleSessionExpiration();
        return;
      }

      // Check if user has been inactive for too long
      const now = Date.now();
      if (now - lastActivity > SESSION_TIMEOUT) {
        await handleSessionExpiration();
      }
    };

    const handleActivity = (): void => {
      const now = Date.now();
      if (now - lastActivity > ACTIVITY_DEBOUNCE) {
        updateActivity();
      }
    };

    const interval = setInterval(checkTimeout, 60 * 1000);

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);

    return () => {
      clearInterval(interval);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
    };
  }, [user, lastActivity, updateActivity, setUser, isSessionCacheValid, clearSessionCache, navigate]);
};
